import { useScrollContext } from "@pathwright/ui/src/components/scroll/ScrollView"
import View from "@pathwright/ui/src/components/ui/View"
import classnames from "classnames"
import PropTypes from "prop-types"
import React, { useContext, useEffect } from "react"
import styled from "styled-components"
import FeatureVisibilityOverlay from "../feature/indicator/FeatureVisibilityOverlay"
import CommunityNav from "./CommunityNav"
import useCommunityAreas, {
  CommunityAreaContextProvider
} from "./area/useCommunityAreas"
import {
  COMMUNITY_AREA_ACTIVITY,
  COMMUNITY_AREA_DISCUSSION,
  COMMUNITY_AREA_MEMBERS,
  COMMUNITY_FEATURE_VISIBILITY_MAP
} from "./constants"

export const CommunityContext = React.createContext()

export const useCommunityContext = () => useContext(CommunityContext)

const StyledCommunityViewContainer = styled(View)`
  width: 100%;
  position: relative;
  max-width: 960px;
  margin: 0px auto;
  padding: 0px;
`

const StyledCommunitySections = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  .CommunityAreaContentWrap {
    padding: 10px;
    width: 100%;
    > div {
      width: 100%;
    }
  }

  ${StyledCommunityViewContainer}.community__discussion & .CommunityAreaContentWrap {
    max-width: 700px;
  }
`

const Container = styled.div``

const Community = (props) => {
  const { selectedArea, renderContent } = useCommunityAreas()
  const { resourceId, cohortId } = useCommunityContext()

  // scroll the nearest ScrollView to the top when changing community areas
  const scrollContext = useScrollContext()
  useEffect(() => {
    if (scrollContext) {
      scrollContext.scrollTo(0)
    }
  }, [selectedArea])

  const featureKey = COMMUNITY_FEATURE_VISIBILITY_MAP[selectedArea.id]

  return (
    <StyledCommunityViewContainer
      padding
      className={classnames("Community", `Community__${selectedArea.id}`)}
    >
      <CommunityNav inverted={true} />
      <StyledCommunitySections>
        <Container className="CommunityAreaContentWrap">
          {/* wrapping div necessary for grid styling */}
          {renderContent(props)}
        </Container>
      </StyledCommunitySections>
      {featureKey && !!cohortId && (
        <FeatureVisibilityOverlay
          featureKey={featureKey}
          context={{
            resource_id: resourceId,
            cohort_id: cohortId
          }}
        />
      )}
    </StyledCommunityViewContainer>
  )
}

const CommunityContainer = ({
  resourceId,
  cohortId,
  communityArea,
  onChangeCommunityArea,
  ...props
}) => {
  return (
    <CommunityContext.Provider
      value={{
        resourceId,
        cohortId
      }}
    >
      <CommunityAreaContextProvider
        selectedArea={communityArea}
        onChangeCommunityArea={onChangeCommunityArea}
      >
        <Community {...props} />
      </CommunityAreaContextProvider>
    </CommunityContext.Provider>
  )
}

CommunityContainer.displayName = "Community"

CommunityContainer.propTypes = {
  communityArea: PropTypes.oneOf([
    COMMUNITY_AREA_DISCUSSION,
    COMMUNITY_AREA_MEMBERS,
    COMMUNITY_AREA_ACTIVITY
  ]),
  resourceId: PropTypes.number,
  cohortId: PropTypes.number,
  inverted: PropTypes.bool,
  onSelectDiscussion: PropTypes.func, // for discussion area
  onStartDiscussion: PropTypes.func, // for discussion area
  onChangeCommunityArea: PropTypes.func
}

CommunityContainer.defaultProps = {
  communityArea: COMMUNITY_AREA_DISCUSSION,
  inverted: true
}

export default CommunityContainer
